import React, { useState, useCallback, useMemo } from 'react';
// Removed: import { Clipboard, Plus, Trash2, Edit } from 'lucide-react';
// Replaced with inline SVGs to resolve TS2307 dependency errors

// --- TypeScript Data Structures (Interfaces) ---

/** Defines the structure for a single letter template. */
interface LetterTemplate {
  id: number;
  title: string;
  emphasis: string;
  template: string;
}

/** Defines the structure for a single child's custom data. */
interface ChildData {
  id: string;
  name: string;
  age: number;
  niceActivity: string;
  goodDeed: string;
  grownUpTitle: string;
  templateId: number;
}

// --- Letter Templates Definition (Typed Object) ---

const SANTA_LETTER_TEMPLATES: LetterTemplate[] = [
  // Placeholders: {{CHILD_NAME}}, {{CHILD_AGE}}, {{NICE_ACTIVITY}}, {{GOOD_DEED}}, {{GROWN_UP_TITLE}}
  { id: 1, title: "1. The Classic North Pole Message", emphasis: "Classic & Traditional", template: `Dearest {{CHILD_NAME}},

Ho ho ho! I hope this special message finds you well! I'm writing to you all the way from my workshop here at the **North Pole**. It's a very busy place right now!

I hear you've just turned {{CHILD_AGE}} (or are about to). My goodness, you are growing up so fast! It seems like just yesterday you were {{NICE_ACTIVITY}}.

I've been keeping a close eye on my List, and I see you've been working hard to stay on the **Nice List** this year. My head elf, Jingle, showed me how you were a **big helper** when you {{GOOD_DEED}}. That makes me so proud! Being kind and trying your best is the most important magic of all.

Right now, the elves are making toys faster than a speeding sleigh, and the reindeer are practicing their takeoffs! Remember to keep shining bright, listening to your {{GROWN_UP_TITLE}}, and going to bed early.

Keep up the great work, my dear {{CHILD_NAME}}!

Yours forever and always, Santa Claus.` },

  { id: 2, title: "2. A Note on Kindness & Sharing", emphasis: "Focus on Empathy", template: `Hello there, {{CHILD_NAME}}!

Mrs. Claus and I wanted to send a quick note about your wonderful heart. We saw how kindly you behaved when you {{GOOD_DEED}}. Kindness is the greatest gift of all! It's better than any toy.

I also saw you've been practicing {{NICE_ACTIVITY}}—that's dedication! Keep listening to your {{GROWN_UP_TITLE}} and spreading cheer. Merry Christmas, from your friend in red!` },

  { id: 3, title: "3. Christmas Eve Excitement", emphasis: "Building Anticipation", template: `Ho Ho Ho! The sleigh bells are getting polished, {{CHILD_NAME}}!

Can you believe Christmas Eve is almost here? I've heard you've been working hard on {{NICE_ACTIVITY}}, which is excellent! My team is loading up the sleigh, and I can't wait to fly over your roof.

It's clear from {{GOOD_DEED}} that you deserve a spot right at the top of the Nice List. Be sure to be extra good for your {{GROWN_UP_TITLE}} right up until bedtime! We'll see you soon!` },

  { id: 4, title: "4. Rudolph's Special Message", emphasis: "Mentions Reindeer & Magic", template: `Dear {{CHILD_NAME}},

Rudolph just flew by my window, and he insisted I write this letter! He said he was very impressed when he heard about how you handled {{GOOD_DEED}}. That kind of effort makes the reindeer fly faster!

Keep shining bright, just like Rudolph's nose, and continue to learn and grow, especially with {{NICE_ACTIVITY}}. Keep listening to your {{GROWN_UP_TITLE}}!

Your friend, Santa. P.S. Don't forget the carrot!` },

  { id: 5, title: "5. Short & Sweet Naughty/Nice Check", emphasis: "Simple & Direct", template: `Hi {{CHILD_NAME}}!

A quick check-in from the workshop. My list confirms you are firmly on the **Nice List**! Keep up the great work, especially when you are doing things like {{GOOD_DEED}}.

We'll see you soon! Love, Santa, and all the Elves.` },

  { id: 6, title: "6. Focused on Learning & Growth", emphasis: "Encourages Education", template: `Greetings from the Elves' University, {{CHILD_NAME}}!

I know you've been learning so much this year. We were especially impressed with your dedication to {{NICE_ACTIVITY}}. Keep reading, learning, and trying new things—that's what being a clever kid is all about!

Remember to help your {{GROWN_UP_TITLE}} when you can! Best wishes, Santa.` },

  { id: 7, title: "7. From the Head Elf's Desk", emphasis: "Elf/Workshop Focus", template: `Dearest {{CHILD_NAME}},

This is Jingle the Head Elf writing! Santa is busy taste-testing cookies, so I'm sending this note. He told me to remind you how proud he is of how you {{GOOD_DEED}}. That makes our toy-making efforts worthwhile!

We hear you are {{CHILD_AGE}} now—a very important age! We're building special things just for you. From Jingle and Santa. P.S. Listen to your {{GROWN_UP_TITLE}}!` },

  { id: 8, title: "8. Thankful for Help at Home", emphasis: "Appreciation for Chores", template: `Hello {{CHILD_NAME}},

The North Pole is crazy right now! I want to thank you for being such a good helper at home, particularly with {{GOOD_DEED}}. When children are helpful, it makes my job easier and the world brighter.

Keep up the amazing work for your {{GROWN_UP_TITLE}}! We see everything! Ho ho ho!` },

  { id: 9, title: "9. Simple Holiday Greeting", emphasis: "Very short & Cheerful", template: `Merry Christmas, {{CHILD_NAME}}!

Just a cheerful hello from my home to yours. I hear you've been a super kid this year and that you were trying hard at {{NICE_ACTIVITY}}. That's what we like to see!

Keep being kind and enjoying your holiday season. I'll be there soon! Santa.` },

  { id: 10, title: "10. Warmest Wishes & Personal Pride", emphasis: "Focus on determination", template: `Dearest {{CHILD_NAME}},

I wanted to send the warmest wishes to you and your family. I'm so proud of the young person you are becoming. Remember when you were working on {{NICE_ACTIVITY}}? That determination is fantastic!

And thank you for being a big helper with {{GOOD_DEED}}. That makes me so happy. I can't wait to bring magic to your house. Love, Santa and Mrs. Claus.` }
];

// --- Utility Functions (Typed) ---

/**
 * Finds a template by its ID.
 * @param id - The template ID.
 * @returns The template object or the first one as default.
 */
const getTemplateById = (id: number): LetterTemplate => SANTA_LETTER_TEMPLATES.find(t => t.id === id) || SANTA_LETTER_TEMPLATES[0];

// --- Main App Component ---
export default function App_Santa_v02(): JSX.Element {
  // State is now strictly typed using the interfaces defined above
  const [children, setChildren] = useState<ChildData[]>([]);
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<number>(1);
  const [copyStatus, setCopyStatus] = useState<boolean>(false);

  /**
   * Generates a letter for a given child and template.
   */
  const generateLetterText = useCallback((childData: ChildData, templateData: LetterTemplate): string => {
    if (!childData.name || !childData.age) {
      return "Please complete the child's details to generate the letter!";
    }
    
    let letter = templateData.template;

    letter = letter.replace(/{{CHILD_NAME}}/g, childData.name);
    letter = letter.replace(/{{CHILD_AGE}}/g, childData.age.toString());
    letter = letter.replace(/{{NICE_ACTIVITY}}/g, childData.niceActivity);
    letter = letter.replace(/{{GOOD_DEED}}/g, childData.goodDeed);
    letter = letter.replace(/{{GROWN_UP_TITLE}}/g, childData.grownUpTitle);

    return letter;
  }, []);

  /**
   * Adds a new child to the list with default data.
   */
  const addChild = (): void => {
    const newChild: ChildData = {
      id: crypto.randomUUID(),
      name: `Child ${children.length + 1}`,
      age: 6,
      niceActivity: 'learning to tie their shoes',
      goodDeed: 'helped clean up their room without being asked',
      grownUpTitle: 'Grown-Ups',
      templateId: 1,
    };
    setChildren([...children, newChild]);
    setActiveChildId(newChild.id);
  };

  /**
   * Removes a child by ID.
   */
  const removeChild = (id: string): void => {
    setChildren(prev => prev.filter(child => child.id !== id));
    if (activeChildId === id) {
      setActiveChildId(null);
    }
  };

  /**
   * Handles form input changes for the active child.
   */
  const handleChildChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setChildren(prev => prev.map(child => {
      if (child.id === activeChildId) {
        return {
          ...child,
          // Safely parse number for age field
          [name]: name === 'age' ? parseInt(value) || 0 : value,
        } as ChildData; // Asserting the type back to ChildData
      }
      return child;
    }));
  };

  /**
   * Handles selection of a new template for the active child.
   */
  const handleTemplateSelection = (templateId: number): void => {
    if (activeChildId) {
      setChildren(prev => prev.map(child => {
        if (child.id === activeChildId) {
          return { ...child, templateId };
        }
        return child;
      }));
    }
    setPreviewTemplateId(templateId);
  };

  /**
   * Memoized data for the currently active child.
   */
  const activeChild: ChildData | undefined = useMemo(() => {
    return children.find(child => child.id === activeChildId);
  }, [children, activeChildId]);

  /**
   * Memoized data for the current letter preview (gets the appropriate template based on active child or preview state).
   */
  const currentTemplate: LetterTemplate = useMemo(() => {
    const id = activeChild ? activeChild.templateId : previewTemplateId;
    return getTemplateById(id);
  }, [activeChild, previewTemplateId]);

  /**
   * Memoized data for the final generated letter text.
   */
  const finalLetter: string = useMemo(() => {
    if (activeChild && currentTemplate) {
      return generateLetterText(activeChild, currentTemplate);
    }
    return '';
  }, [activeChild, currentTemplate, generateLetterText]);

  /**
   * Copies the final generated letter text to the clipboard.
   */
  const copyToClipboard = (): void => {
    try {
      const el = document.createElement('textarea');
      el.value = finalLetter;
      document.body.appendChild(el);
      el.select();
      // Using execCommand for better cross-browser compatibility in iFrames
      document.execCommand('copy'); 
      document.body.removeChild(el);
      setCopyStatus(true);
      setTimeout(() => setCopyStatus(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // --- Child Selector Component ---
  const ChildSelector = (): JSX.Element => (
    <div className="mb-6">
      <h3 className="text-xl font-bold text-red-700 mb-3 border-b pb-2 border-red-200 flex justify-between items-center">
        1. Manage Children
        <button
          onClick={addChild}
          className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors shadow-md flex items-center text-sm"
          aria-label="Add New Child"
        >
          {/* Plus Icon (Inline SVG) */}
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-1"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>
          Add Child
        </button>
      </h3>
      <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
        {children.length === 0 ? (
          <p className="text-gray-500 italic">Click "Add Child" to begin!</p>
        ) : (
          children.map((child: ChildData) => (
            <div
              key={child.id}
              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
                child.id === activeChildId
                  ? 'bg-yellow-100 border-2 border-yellow-500 shadow-lg'
                  : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
              }`}
              onClick={() => setActiveChildId(child.id)}
            >
              <span className={`font-semibold ${child.id === activeChildId ? 'text-red-700' : 'text-gray-800'}`}>
                {child.name || 'Unnamed Child'} (Age: {child.age})
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveChildId(child.id); }}
                  className="p-1 text-blue-500 hover:text-blue-700"
                  title="Edit Child Details"
                >
                  {/* Edit Icon (Inline SVG) */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); removeChild(child.id); }}
                  className="p-1 text-red-500 hover:text-red-700"
                  title="Remove Child"
                >
                  {/* Trash2 Icon (Inline SVG) */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // --- Template Selector Component ---
  const TemplateSelector = (): JSX.Element => (
    <div className="mb-6">
      <h3 className="text-xl font-bold text-red-700 mb-3 border-b pb-2 border-red-200">
        2. Choose Letter Template
      </h3>
      <div className="space-y-2 max-h-56 overflow-y-auto pr-2">
        {SANTA_LETTER_TEMPLATES.map((template: LetterTemplate) => (
          <div
            key={template.id}
            className={`p-3 rounded-xl cursor-pointer transition-all border ${
              template.id === (activeChild ? activeChild.templateId : previewTemplateId)
                ? 'bg-red-100 border-red-500 shadow-md ring-2 ring-red-500'
                : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
            }`}
            onClick={() => handleTemplateSelection(template.id)}
          >
            <p className={`font-semibold ${template.id === activeChild?.templateId ? 'text-red-700' : 'text-gray-800'}`}>
              {template.title}
            </p>
            <p className="text-xs text-gray-500 italic">({template.emphasis})</p>
            {activeChild && template.id === activeChild.templateId && (
              <span className="text-xs font-bold text-green-600 block mt-1">
                Selected for {activeChild.name}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // --- Child Details Editor Component ---
  const ChildDetailsEditor = (): JSX.Element => {
    if (!activeChild) {
      return (
        <div className="p-5 bg-blue-50 rounded-xl border-2 border-blue-200 text-blue-800 text-center italic">
          Select a child above to edit their details and select their letter.
        </div>
      );
    }

    return (
      <div className="space-y-5 mt-6 border-t pt-4 border-red-200">
        <h3 className="text-xl font-bold text-red-700 mb-3 flex items-center">
          3. Customize Details for: <span className="ml-2 px-3 py-1 bg-yellow-300 text-red-800 rounded-full text-sm">{activeChild.name}</span>
        </h3>

        {/* Child's Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Child's Full Name
          </label>
          <input
            type="text"
            name="name"
            id="name"
            value={activeChild.name}
            onChange={handleChildChange}
            placeholder="Enter child's name"
            required
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
          />
        </div>

        {/* Child's Age & Grown-Up Title */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
              Child's Age
            </label>
            <input
              type="number"
              name="age"
              id="age"
              value={activeChild.age}
              onChange={handleChildChange}
              min="1"
              max="100"
              required
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
            />
          </div>
          <div>
            <label htmlFor="grownUpTitle" className="block text-sm font-medium text-gray-700 mb-1">
              Adult's Title
            </label>
            <select
              name="grownUpTitle"
              id="grownUpTitle"
              value={activeChild.grownUpTitle}
              onChange={handleChildChange}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
            >
              <option value="Mom">Mom</option>
              <option value="Dad">Dad</option>
              <option value="Grandma">Grandma</option>
              <option value="Grandpa">Grandpa</option>
              <option value="Grown-Ups">Grown-Ups (default)</option>
            </select>
          </div>
        </div>

        {/* Simple Nice Activity */}
        <div>
          <label htmlFor="niceActivity" className="block text-sm font-medium text-gray-700 mb-1">
            Simple Nice Activity (e.g., 'learned to tie their shoes')
          </label>
          <textarea
            name="niceActivity"
            id="niceActivity"
            rows={2} // Ensures the rows prop is a number, not a string
            value={activeChild.niceActivity}
            onChange={handleChildChange}
            placeholder="e.g., learned to sing the ABCs"
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
          ></textarea>
        </div>

        {/* Specific Good Deed */}
        <div>
          <label htmlFor="goodDeed" className="block text-sm font-medium text-gray-700 mb-1">
            Specific **Good Deed** (The 'Big Helper' part!)
          </label>
          <textarea
            name="goodDeed"
            id="goodDeed"
            rows={2} // Ensures the rows prop is a number, not a string
            value={activeChild.goodDeed}
            onChange={handleChildChange}
            placeholder="e.g., shared their favorite toy with their little sister"
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500 text-gray-900"
          ></textarea>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4 sm:p-8 bg-gradient-to-br from-red-700 via-red-800 to-red-900 font-inter text-gray-100">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-10 p-4 rounded-xl bg-white bg-opacity-10 shadow-lg">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-yellow-300 drop-shadow-lg">
            🦌 The Elves' Personalized Letter Workshop (TSX)
          </h1>
          <p className="mt-2 text-lg text-red-200">
            Create unique, magical letters for every child on your Nice List!
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* --- Input Form (Left Side) --- */}
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl border-4 border-yellow-300">
            <h2 className="text-2xl font-bold text-red-800 mb-6 border-b pb-2 border-red-200">
              Personalization Center
            </h2>

            <ChildSelector />
            <TemplateSelector />
            <ChildDetailsEditor />

            {/* Hint Box for Active Child */}
            {activeChild && (
                <div className="mt-6 p-4 bg-green-50 rounded-xl border-2 border-green-200 text-green-800 text-sm">
                    **Note:** The letter displayed on the right is customized for **{activeChild.name}** using the **{getTemplateById(activeChild.templateId).title}** template.
                </div>
            )}
          </div>

          {/* --- Generated Letter Output (Right Side) --- */}
          <div className="relative bg-white p-6 sm:p-8 rounded-2xl shadow-2xl border-4 border-green-300">
            <h2 className="text-2xl font-bold text-green-800 mb-6 border-b pb-2 border-green-200">
              Letter Preview & Download
            </h2>
            <p className="text-sm text-gray-600 mb-4">
                **Current Preview:** {activeChild ? `${activeChild.name}'s Letter (${currentTemplate.title})` : `Template Preview (${currentTemplate.title})`}
            </p>

            {activeChild ? (
              <>
                <div className="overflow-y-auto max-h-[70vh] p-4 bg-red-50 border border-red-200 rounded-xl text-gray-900 whitespace-pre-wrap font-serif text-base leading-relaxed shadow-inner">
                  {finalLetter}
                </div>

                <div className="mt-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                  <button
                    onClick={copyToClipboard}
                    className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-yellow-500 text-white font-bold rounded-xl hover:bg-yellow-600 transition-colors shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-yellow-400"
                  >
                    {/* Clipboard Icon (Inline SVG) */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path></svg>
                    {copyStatus ? 'Copied!' : `Copy Letter for ${activeChild.name}`}
                  </button>
                  {copyStatus && (
                    <span className="text-sm text-yellow-300 bg-green-700 px-3 py-1 rounded-full shadow-md animate-pulse">
                      Text copied to clipboard!
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-48 bg-gray-100 rounded-xl text-gray-500 border-2 border-dashed border-gray-300">
                <p className="text-lg text-center">
                  Start by clicking **"Add Child"** in the left panel to begin creating a personalized letter!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
